import { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Library, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { admin, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (admin) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.userMessage || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4 py-10 relative overflow-hidden">
      {/* Decorative gradient */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 size-[420px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 size-[420px] rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-xl p-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-7">
          <div className="size-11 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Library className="size-6 text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold tracking-tight">The Greater Life</span>
            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              Admin Console
            </span>
          </div>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sign in to manage devotionals, users and subscriptions.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="admin@greaterlife.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-10"
          >
            {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-xs text-muted-foreground text-center">
          Protected area • Authorized administrators only
        </p>
        <p className="mt-2 text-xs text-center">
          <Link
            to="/privacy"
            data-testid="login-privacy-link"
            className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline transition-colors"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
